import { StringContext } from "../StringContext";
import { ScrollController } from "./ScrollController";

type SmoothComputeStepResult = {
  current: number;
  target: number;
  delta: number;
  lerped: number;
  scrollForce: number;
  absVelocity: number;
};

export class StringScrollSmooth extends ScrollController {
  public readonly name: string = "smooth";

  private scrollForce: number = 0.0;
  private wheelImpulse: number = 0.0;
  private previousCurrent: number = 0.0;
  private scrollToSequence: number = 0;
  private activeScrollToId: number | null = null;

  private readonly stepResult: SmoothComputeStepResult = {
    current: 0.1,
    target: 0.1,
    delta: 0.1,
    lerped: 0.1,
    scrollForce: 0.1,
    absVelocity: 0.1,
  };

  constructor(context: StringContext) {
    super(context);
    this.stepResult.current = 0.0;
    this.stepResult.target = 0.0;
    this.stepResult.delta = 0.0;
    this.stepResult.lerped = 0.0;
    this.stepResult.scrollForce = 0.0;
    this.stepResult.absVelocity = 0.0;
  }

  public stopScroll(): void {
    const scrollData = this.context.data.scroll;

    scrollData.lerped = 0.0;
    scrollData.delta = 0.0;
    scrollData.target = scrollData.current;
    this.onCalcUpdate();
    this.isProg = false;
    this.clearScrollingClasses();
    this._scrollDirState = -1;
    this._lastAppliedDirState = -1;
    this.activeScrollToId = null;
  }

  public onFrame(): void {
    const scrollData = this.context.data.scroll;

    if (scrollData.delta !== 0.0) {
      this.computeStep(
        scrollData.current,
        scrollData.target,
        scrollData.delta,
        scrollData.speed,
        scrollData.speedAccelerate,
        scrollData.decelerationCoefficient,
        scrollData.maxDelta,
        scrollData.bottomPosition,
        this.stepResult,
      );

      this.scrollForce = this.stepResult.scrollForce;
      scrollData.target = this.stepResult.target;
      scrollData.delta = this.stepResult.delta;
      scrollData.lerped = this.stepResult.lerped;
      scrollData.current = this.stepResult.current;

      const scale = this.context.data.viewport.transformScale;
      scrollData.transformedCurrent =
        scale !== 1.0 ? scrollData.current * scale : scrollData.current;

      this.updateScrollDirection(scrollData.lerped > 0.0);

      if (this.stepResult.absVelocity < scrollData.stopThreshold) {
        scrollData.current = Math.round(scrollData.target);
        this.previousCurrent = scrollData.current;
        this.onCalcUpdate();
        this.stopScroll();
        this.onScrollStop();
      } else {
        this.isProg = true;

        if (this.previousCurrent !== scrollData.current) {
          this.previousCurrent = scrollData.current;
          this.onCalcUpdate();
        }
      }
    }
  }

  public onWheel(e: WheelEvent): void {
    if (e.deltaY !== 0.0) {
      e.preventDefault();
    }

    this.wheelImpulse = e.deltaY;
    if (this.wheelImpulse === 0.0) return;

    const scrollData = this.context.data.scroll;

    if (scrollData.delta === 0.0) {
      this.onScrollStart();
    }

    const isScrollingUp = this.wheelImpulse < 0;
    const atTop = scrollData.target === 0.0 && isScrollingUp;
    const atBottom = scrollData.target === scrollData.bottomPosition && !isScrollingUp;

    if (!atTop && !atBottom) {
      scrollData.delta += this.wheelImpulse * scrollData.multiplier;
    }
  }

  public onScroll(e: Event): void {
    if (!this.isProg) {
      const scrollData = this.context.data.scroll;
      const nativeScrollTop = scrollData.elementContainer.scrollTop;
      const positionDelta = nativeScrollTop - scrollData.current;

      scrollData.current = nativeScrollTop;
      scrollData.target = nativeScrollTop;
      scrollData.delta = 0.0;
      scrollData.lerped = positionDelta;
      scrollData.displacement = 0.0;

      const scale = this.context.data.viewport.transformScale;
      scrollData.transformedCurrent = scale !== 1.0 ? nativeScrollTop * scale : nativeScrollTop;

      this.scrollForce = 0.0;
      this.wheelImpulse = 0.0;
      this.isProg = false;
      this.previousCurrent = nativeScrollTop;
      this.activeScrollToId = null;

      if (positionDelta !== 0.0) {
        this.updateScrollDirection(positionDelta > 0.0);
        this.triggerScrollRules();
      }
    }
  }

  public deactivate(): void {
    super.deactivate();
    this.scrollForce = 0.0;
    this.wheelImpulse = 0.0;
    this.previousCurrent = this.context.data.scroll.current;
  }

  public scrollTo(position: number, immediate?: boolean) {
    const scrollData = this.context.data.scroll;
    const scrollToId = ++this.scrollToSequence;
    this.activeScrollToId = scrollToId;

    if (immediate) {
      scrollData.current = position;
      scrollData.target = position;
      scrollData.delta = 0.0;
      scrollData.lerped = 0.0;

      const scale = this.context.data.viewport.transformScale;
      scrollData.transformedCurrent = scale !== 1.0 ? position * scale : position;

      this.onCalcUpdate();
      return;
    }

    scrollData.target = position;
    scrollData.delta = 1.0;
  }

  private computeStep(
    current: number,
    target: number,
    delta: number,
    speed: number,
    speedAccelerate: number,
    decayPower: number,
    maxDelta: number,
    bottomPosition: number,
    out: SmoothComputeStepResult,
  ): void {
    const clampedDelta = this.clamp(delta, -maxDelta, maxDelta);
    const scrollForce = clampedDelta * speedAccelerate;
    const decayFactor = Math.pow(1 - speedAccelerate, decayPower);

    const nextTarget = Math.min(Math.max(0.0, target + scrollForce), bottomPosition);
    const lerped = (nextTarget - current) * speed;
    const nextDelta = clampedDelta * decayFactor;

    const nextCurrent = current + lerped;

    out.current = nextCurrent;
    out.target = nextTarget;
    out.delta = nextDelta;
    out.lerped = lerped;
    out.scrollForce = scrollForce;
    out.absVelocity = Math.abs(lerped);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }
}
